/* eslint-disable @typescript-eslint/no-explicit-any */
import { createSelector } from "@reduxjs/toolkit";
import { RootState } from "@/redux/store";

export const operatorsSelector = (state: RootState) => state.operator.operators;
export const activeOperatorSelector = (state: RootState) => state.operator.activeOperator;
export const operatorIdSelector = (state: any) => state.operator.activeOperator?.id;
export const defaultCountrySelector = (state: any) => state.operator.activeOperator?.country;

/** Google Places accepts at most five codes in one `componentRestrictions.country`. */
const GOOGLE_PLACES_MAX_COUNTRIES = 5;

const normaliseCountryCodes = (codes: Array<string | undefined>): string[] =>
  Array.from(new Set(codes.map((code) => (code ?? "").trim().toLowerCase()).filter((code) => code.length > 0))).slice(
    0,
    GOOGLE_PLACES_MAX_COUNTRIES
  );

/**
 * Countries an OPERATOR may book in for this provider: `bookable_country_list` (the domain's
 * `d-operates-in-countries`, e.g. AU and NZ for Royale), falling back to the provider's base country when
 * the list is empty. Lower-cased and deduped; memoised on the active operator so it is stable in effect
 * dependencies (DEV-1606).
 */
export const operatorBookingCountriesSelector = createSelector([activeOperatorSelector], (operator): string[] => {
  const list = normaliseCountryCodes(operator?.bookableCountryList ?? []);
  return list.length ? list : normaliseCountryCodes([operator?.country]);
});

/**
 * Countries a CLIENT (self-service, guest, EA on behalf) may book in: the provider's base country only,
 * falling back to the bookable list if the base country is missing. Empty only when the provider has
 * neither, in which case the address search returns nothing rather than searching the world (DEV-1606).
 */
export const clientBookingCountriesSelector = createSelector([activeOperatorSelector], (operator): string[] => {
  const base = normaliseCountryCodes([operator?.country]);
  return base.length ? base : normaliseCountryCodes(operator?.bookableCountryList ?? []);
});
